import React from 'react';
import './index.scss';
import { Select, LoadingSpinner, CodeBlock, AlertError } from 'plaid-threads';
import {
  PRODUCT_ENDPOINT_OPTIONS,
  ProductEndpointsType,
  productEndpoints,
} from './productEndpoints';
import TabData from './TabData';
import { AppContext } from '../App';

interface ProductEndpointDataProps {
  product: ProductEndpointsType;
  setError: React.Dispatch<React.SetStateAction<string>>;
  userFullName: string;
  transactionDays: number;
  pfcVersion?: string;
}
interface TabDataProps {
  jsonData: Object;
  pdfData: string;
}

export const ProductEndpointData: React.FC<ProductEndpointDataProps> = (
  props
) => {
  const [loading, setLoading] = React.useState(true);
  const [data, setData] = React.useState();
  const { product, setError, userFullName, transactionDays, pfcVersion } = props;

  React.useEffect(() => {
    async function fetchData() {
      try {
        const res = await productEndpoints[product]({
          userFullName,
          transactionDays,
          pfcVersion,
        });
        setData(res);
      } catch (e: any) {
        setError(e);
      }

      setLoading(false);
    }

    setData(undefined);
    setLoading(true);
    setError('');
    fetchData();
  }, [product, setError, userFullName, transactionDays, pfcVersion]);

  const title =
    product === 'identitymatch'
      ? 'Identity Match'
      : PRODUCT_ENDPOINT_OPTIONS.find((p) => p.value === product)?.label;

  return (
    <div className="Products__endpointData">
      {loading && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <LoadingSpinner />
          <div style={{ marginTop: '8px', fontSize: '14px', color: '#666' }}>
            We are processing your data, this may take a few seconds.
          </div>
        </div>
      )}
      {data ? (
        product === 'assets' || product === 'income_verification' ? (
          <TabData
            pdfData={(data as TabDataProps).pdfData}
            jsonData={(data as TabDataProps).jsonData}
            title={title}
          />
        ) : (
          <CodeBlock
            code={JSON.stringify(data, null, 2)}
            onClick={() => navigator.clipboard.writeText(JSON.stringify(data, null, 2))}
            title={`${title} Response Data`}
          />
        )
      ) : null}
    </div>
  );
};

interface ProductsProps {
  productExperience: string | undefined;
  userFullName: string;
}

const Products: React.FC<ProductsProps> = (props) => {
  const { accountProducts, pfcVersion } = React.useContext(AppContext);
  const isInvestmentsOnly =
    accountProducts.length === 1 && accountProducts[0] === 'investments';
  const productOptions = isInvestmentsOnly
    ? PRODUCT_ENDPOINT_OPTIONS.filter((p) => p.value === 'investments')
    : PRODUCT_ENDPOINT_OPTIONS;
  const [
    selectedProduct,
    setSelectedProduct,
  ] = React.useState<ProductEndpointsType>(
    isInvestmentsOnly ? 'investments' : 'auth'
  );
  const [error, setError] = React.useState('');
  const { productExperience, userFullName} = props;
  const isIdentityMatch = productExperience === 'identitymatch';
  return (
    <div className="Products">
      <div className="Products__title">Thank you!</div>
      <div className="Products__instructions">
        {!isIdentityMatch &&
          'Select a product endpoint to retrieve response data'}
      </div>
      {!isIdentityMatch && (
        <Select
          id="productEndpointSelection"
          className="Products__selection"
          controlClassName={error ? 'error' : ''}
          label="Product Endpoint"
          options={productOptions}
          value={productOptions.find(
            (p) => p.value === selectedProduct
          )}
          onChange={(e: any) => setSelectedProduct(e.value)}
        />
      )}
      <ProductEndpointData
        product={isIdentityMatch ? 'identitymatch' : selectedProduct}
        setError={setError}
        userFullName={userFullName}
        transactionDays={90}
        pfcVersion={pfcVersion}
      />
      {error && (
        <div className="Products__error">
          <span className="Products__errorIcon">
            <AlertError />
          </span>
          Error while processing. Please choose a different product.
        </div>
      )}
    </div>
  );
};

export default Products;
